import { Button, CloseButton } from '@ui/shared/Button';
import * as React from 'react';
import styled from 'styled-components';
import { LIST_ROW_HEIGHTS } from '../../../constants/list_row_heights';
import apiKeyProvider from '../../../data/providers/apiKeyProvider';
import { IApiKey, ITeam } from '../../../data/types';
import { fullDateWithTime } from '../../../utils/date';
import { useEntities } from '../../../hooks/useEntities';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { ScrollableArea } from '../../layout/ScrollableArea';
import { CardFooter } from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { List } from '../../shared/List/List';
import { ListHeader } from '../../shared/List/ListHeader';
import {
  ListItem,
  ListItemCell,
  ListItemSafeText,
} from '../../shared/List/ListItem';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { NoResults } from '../../shared/NoResults';
import { PopupCard, PopupCardContent } from '../../shared/popups/PopupCard';
import { AddKeyPopup } from './AddKeyPopup';
import { DeleteKeyPopup } from './DeleteKeyPopup';
import { EditKeyPopup } from './EditKeyPopup';

interface IProps {
  onClose: () => void;
}

const TOOLTIP_VERTICAL_OFFSET = 35; // Position tooltip above the element

const ScopeContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const ScopePill = styled.span`
  display: inline-block;
  font-size: 0.65em;
  padding: 1px 6px;
  border-radius: 8px;
  background-color: var(--color-surface-6, rgba(255, 255, 255, 0.1));
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
`;

const Tooltip = styled.div`
  display: none;
  position: fixed;
  z-index: 10000;
  background: var(--color-surface-8, #2c2c2c);
  border: 1px solid var(--color-separator);
  border-radius: 4px;
  padding: 8px 12px;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  color: var(--color-text);
  font-size: 13px;
  pointer-events: none;
`;
/**
 *
 * @description manages api keys
 * @param {IProps} props
 * @returns {React.ReactElement}
 */
export const Integrations = (props: IProps): React.ReactElement => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [keys, setKeys] = React.useState<IApiKey[]>([]);
  const { teams } = useEntities();

  /**
   * @description subscribes to api keys
   */
  React.useEffect((): (() => void) => {
    const subscriber = apiKeyProvider().subscribe((mKeys: IApiKey[]): void => {
      const activeKeys = mKeys.filter((key: IApiKey): boolean => {
        return key.expiresAt > Date.now();
      });
      setKeys(activeKeys);
      setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, []);


  /**
   * @description gets the scope display as an array of team names
   * @param apiKey
   */
  const getTokenScopeLabels = (apiKey: IApiKey): string[] => {
    if (!apiKey.teamIds || apiKey.teamIds.length === 0) {
      return ['All Teams'];
    }
    return apiKey.teamIds.map((teamId: string): string => {
      const team = teams.find((t: ITeam): boolean => t.id === teamId);
      return team ? team.name : teamId;
    });
  };

  const [tooltipData, setTooltipData] = React.useState<{
    show: boolean;
    text: string;
    x: number;
    y: number;
    id: string;
  }>({ show: false, text: '', x: 0, y: 0, id: '' });

  /**
   * @description renders list rows
   * @param param0
   */
  const rowRenderer = ({
    index,
    key,
    style,
  }: {
    index: number;
    key: string;
    style: React.CSSProperties;
  }): React.ReactElement => {
    const row = keys[index];
    const scopeLabels = getTokenScopeLabels(row);
    const tooltipId = `tooltip-${row.id}`;

    const showTooltip = (e: React.MouseEvent | React.FocusEvent): void => {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipData({
        show: true,
        text: scopeLabels.join(', '),
        x: rect.left,
        y: rect.top - TOOLTIP_VERTICAL_OFFSET,
        id: tooltipId,
      });
    };

    const hideTooltip = (): void => {
      setTooltipData((prev): typeof tooltipData => ({ ...prev, show: false }));
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        showTooltip(e as unknown as React.FocusEvent);
      }
    };

    return (
      <ListItem style={style} key={key}>
        <ListItemCell style={{ alignSelf: 'flex-start', paddingTop: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <ListItemSafeText style={{ marginBottom: '4px' }}>{row.appName}</ListItemSafeText>
            <ScopeContainer
              onMouseEnter={showTooltip}
              onMouseLeave={hideTooltip}
              onFocus={showTooltip}
              onBlur={hideTooltip}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              aria-describedby={
                tooltipData.show && tooltipData.id === tooltipId
                  ? tooltipId
                  : undefined
              }
            >
              {scopeLabels.slice(0, 3).map((label: string): React.ReactElement => (
                <ScopePill key={label}>{label}</ScopePill>
              ))}
              {scopeLabels.length > 3 && (
                <ScopePill>+{scopeLabels.length - 3} more</ScopePill>
              )}
            </ScopeContainer>
          </div>
        </ListItemCell>
        <ListItemCell style={{ alignSelf: 'flex-start', paddingTop: '8px' }}>
          <ListItemSafeText>{row.key.split('.')[0]}</ListItemSafeText>
        </ListItemCell>
        <ListItemCell style={{ alignSelf: 'flex-start', paddingTop: '8px' }}>
          <ListItemSafeText>
            {row.lastUsed ? fullDateWithTime(row.lastUsed) : 'Never'}
          </ListItemSafeText>
        </ListItemCell>
        <ListItemCell style={{ alignSelf: 'flex-start', paddingTop: '8px' }}>
          <ListItemSafeText>{fullDateWithTime(row.expiresAt)}</ListItemSafeText>
        </ListItemCell>
        <ListItemCell style={{ alignSelf: 'flex-start', paddingTop: '8px' }}>
          <EditKeyPopup apiKey={row} />
          <DeleteKeyPopup apiKey={row} />
        </ListItemCell>
      </ListItem>
    );
  };

  return (
    <PopupCard size='large' onClose={props.onClose} noClickOutside>
      {tooltipData.show && (
        <Tooltip
          id={tooltipData.id}
          role='tooltip'
          aria-live='polite'
          aria-hidden={!tooltipData.show}
          style={{
            display: 'block',
            left: `${tooltipData.x}px`,
            top: `${tooltipData.y}px`
          }}
        >
          {tooltipData.text}
        </Tooltip>
      )}
      <PopupCardContent>
        <CardHeader surface={8} borderless>
          <CardHeaderLeft>
            <h5>API Keys</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <ScrollableArea
          style={{ backgroundColor: 'var(--color-list-background)' }}
        >
          <CardHeader withListHeader notRounded surface={8}>
            <Column>
              <Row>
                <CardHeaderLeft />
                <CardHeaderRight>
                  <AddKeyPopup />
                </CardHeaderRight>
              </Row>
              <ListHeader
                labels={[
                  'Name',
                  'Key Prefix',
                  'Last Used',
                  'Expires',
                  'Actions',
                ]}
              />
            </Column>
          </CardHeader>
          {loading ? (
            <LinearLoading />
          ) : keys && keys.length > 0 ? (
            <List
              listLength={keys.length}
              rowRenderer={rowRenderer}
              rowHeight={LIST_ROW_HEIGHTS.large}
            />
          ) : (
            <NoResults message='No API Keys Found' />
          )}
        </ScrollableArea>
        <CardFooter />
      </PopupCardContent>
    </PopupCard>
  );
};
